'use client';

import { Info } from 'lucide-react';
import { getItemKind, parseTypeProfile } from '@/lib/menu/item-kinds';
import type { PackageProfile, DrinkProfile, ServiceProfile } from '@/lib/menu/item-kinds/types';
import type { OrderingMenuItem } from '@/lib/menu/ordering/types';
import { getComboProfile } from '@/lib/menu/ordering/utils';
import { formatMoney as formatMoneyCanonical } from '@/lib/money';

// Delegates to lib/money.ts (D3, docs/qa/production-demo-readiness-2026-08-13.md).
function formatMoney(amount: number, currency?: string | null) {
  return formatMoneyCanonical(amount, currency || 'GHS');
}

export function ItemKindDetails({ item, compact }: { item: OrderingMenuItem; compact?: boolean }) {
  const kind = getItemKind(item.kind);
  const { profile } = parseTypeProfile(item.kind, item.type_profile);

  if (kind.id === 'package') {
    const p = profile as PackageProfile;
    if (compact) {
      return (
        <div className="px-4 pb-2.5 text-xs text-[var(--color-text-secondary)] space-y-1">
          {p.tiers?.length ? (
            <p><span className="font-medium text-[var(--color-text-primary)]">Tiers:</span> {p.tiers.map((t) => `${t.name} (${t.price_per_person}/pp)`).join(', ')}</p>
          ) : null}
          {p.deposit_pct ? <p><span className="font-medium text-[var(--color-text-primary)]">Deposit:</span> {p.deposit_pct}%</p> : null}
          {p.min_headcount ? <p><span className="font-medium text-[var(--color-text-primary)]">Min headcount:</span> {p.min_headcount}</p> : null}
          {p.lead_time_days ? <p><span className="font-medium text-[var(--color-text-primary)]">Lead time:</span> {p.lead_time_days}d</p> : null}
        </div>
      );
    }
    return (
      <div className="space-y-1.5 text-sm">
        {p.tiers?.length ? (
          <div>
            <span className="font-medium text-[var(--color-text-primary)]">Tiers</span>
            <div className="mt-1 space-y-1">
              {p.tiers.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-lg bg-[var(--color-bg-sunken)]/50 px-3 py-1.5">
                  <span className="text-[var(--color-text-secondary)]">{t.name}</span>
                  <span className="font-medium text-[var(--color-text-primary)]">{formatMoney(t.price_per_person)} / person</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {p.deposit_pct ? (
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-text-secondary)]">Deposit</span>
            <span className="font-medium text-[var(--color-text-primary)]">{p.deposit_pct}% upfront</span>
          </div>
        ) : null}
        {p.min_headcount ? (
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-text-secondary)]">Minimum guests</span>
            <span className="font-medium text-[var(--color-text-primary)]">{p.min_headcount}</span>
          </div>
        ) : null}
        {p.lead_time_days ? (
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-text-secondary)]">Lead time</span>
            <span className="font-medium text-[var(--color-text-primary)]">{p.lead_time_days} day{p.lead_time_days > 1 ? 's' : ''}</span>
          </div>
        ) : null}
        {p.serves_note ? (
          <p className="text-[var(--color-text-secondary)]">{p.serves_note}</p>
        ) : null}
      </div>
    );
  }

  if (kind.id === 'drink') {
    const p = profile as DrinkProfile;
    if (compact) {
      return (
        <div className="px-4 pb-2.5 text-xs text-[var(--color-text-secondary)] space-y-1">
          {p.abv != null ? <p><span className="font-medium text-[var(--color-text-primary)]">ABV:</span> {p.abv}%</p> : null}
          {p.age_restricted ? <p><span className="font-medium text-[var(--color-text-primary)]">Age restricted</span></p> : null}
        </div>
      );
    }
    return (
      <div className="space-y-1.5 text-sm">
        {p.abv != null ? (
          <div className="flex items-center justify-between">
            <span className="text-[var(--color-text-secondary)]">ABV</span>
            <span className="font-medium text-[var(--color-text-primary)]">{p.abv}%</span>
          </div>
        ) : null}
        {p.age_restricted ? (
          <div className="flex items-center gap-1.5 text-[var(--color-warning)]">
            <Info className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Age restricted</span>
          </div>
        ) : null}
      </div>
    );
  }

  if (kind.id === 'service') {
    const p = profile as ServiceProfile;
    if (compact) {
      return p.unit_label ? (
        <div className="px-4 pb-2.5 text-xs text-[var(--color-text-secondary)]">
          <p><span className="font-medium text-[var(--color-text-primary)]">Unit:</span> {p.unit_label}</p>
        </div>
      ) : null;
    }
    return p.unit_label ? (
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--color-text-secondary)]">Unit</span>
        <span className="font-medium text-[var(--color-text-primary)]">{p.unit_label}</span>
      </div>
    ) : null;
  }

  if (kind.id === 'combo') {
    const comboProfile = getComboProfile(item);
    if (compact) {
      return (
        <div className="px-4 pb-2.5 text-xs text-[var(--color-text-secondary)] space-y-1">
          {comboProfile?.components?.length ? (
            <p><span className="font-medium text-[var(--color-text-primary)]">Includes:</span> {comboProfile.components.map((c) => c.slot).join(', ')}</p>
          ) : null}
        </div>
      );
    }
    return (
      <div className="space-y-1.5 text-sm">
        {comboProfile?.components?.length ? (
          <div>
            <span className="font-medium text-[var(--color-text-primary)]">Includes</span>
            <div className="mt-1 space-y-1">
              {comboProfile.components.map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-[var(--color-bg-sunken)]/50 px-3 py-1.5">
                  <span className="text-[var(--color-text-secondary)]">{c.slot}</span>
                  <span className="font-medium text-[var(--color-text-primary)]">{c.eligible_item_ids.length} option{c.eligible_item_ids.length !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
}
